import { createStore, type StoreApi } from 'zustand';

import type { WizardStepKey } from '../model/step';
import {
  isLaterSegment,
  isTourSegment,
  REPLY_DRAFT_TOUR_SEGMENTS,
  segmentForStep,
  tourZoneKey,
  type ReplyDraftTourPhase,
  type ReplyDraftTourSegment,
  type ReplyDraftTourTrigger,
} from '../model/tour';

/**
 * State for the reply draft tour, one store per open wizard.
 *
 * Pure transitions only. Callout writes, analytics and the tour's own start
 * and stop calls belong to the provider and its actions, which read the
 * transitions' results, so every rule about what the tour may do next is
 * testable without rendering one.
 *
 * `phase` is the segment the tour is waiting to show and `running` the one on
 * screen. They differ while a segment waits for its first target to mount,
 * and when the target leaves mid-segment: the segment is then interrupted
 * rather than finished, so it starts again when its target comes back.
 *
 * The tour's scrim does not block the wizard, so an expert can pick a
 * position mid-tour. `stepChanged` follows them forward to the segment for
 * the step they reached, and never back, so a segment already shown is not
 * shown again on a trip back to the card.
 *
 * Opening a source mid-tour hides the draft under it, targets included, but
 * leaves them mounted. `paused` holds the segment back while the source is
 * open, so the driver takes it off the screen and starts it again from its
 * first step once the draft is back.
 */
type ReplyDraftTourState = {
  phase: ReplyDraftTourPhase;
  running: ReplyDraftTourSegment | null;
  trigger: ReplyDraftTourTrigger;
  /** Mounted targets, keyed by `segment:zone`. */
  zones: Record<string, true>;
  /** Whether a segment of the current run has reached the screen. */
  hasShownThisRun: boolean;
  paused: boolean;
};

type ReplyDraftTourTransitions = {
  /**
   * Starts a run at the segment for the wizard's step. A first-draft run
   * starts only from `idle`; a replay from anywhere but mid-tour. Returns
   * whether it started.
   */
  begin: (trigger: ReplyDraftTourTrigger, step: WizardStepKey) => boolean;
  stepChanged: (step: WizardStepKey) => void;
  setPaused: (paused: boolean) => void;
  setZone: (key: string, isMounted: boolean) => void;
  /** Records a segment reaching the screen. Returns whether it is the run's first. */
  segmentStarted: (segment: ReplyDraftTourSegment) => boolean;
  segmentInterrupted: (segment: ReplyDraftTourSegment) => void;
  /** Advances past a finished segment. Returns the phase it moved to. */
  finishSegment: (segment: ReplyDraftTourSegment) => ReplyDraftTourPhase;
  skip: () => void;
};

export type ReplyDraftTourStore = ReplyDraftTourState & ReplyDraftTourTransitions;

export function createReplyDraftTourStore(): StoreApi<ReplyDraftTourStore> {
  return createStore<ReplyDraftTourStore>((set, get) => ({
    phase: 'idle',
    running: null,
    trigger: 'auto',
    zones: {},
    hasShownThisRun: false,
    paused: false,

    begin: (trigger, step) => {
      const { phase } = get();

      if (isTourSegment(phase) || (trigger === 'auto' && phase !== 'idle')) {
        return false;
      }

      set({ phase: segmentForStep(step), trigger, hasShownThisRun: false });

      return true;
    },

    stepChanged: (step) => {
      const { phase } = get();

      const reached = segmentForStep(step);

      if (isTourSegment(phase) && isLaterSegment(reached, phase)) {
        set({ phase: reached });
      }
    },

    setPaused: (paused) => {
      if (get().paused !== paused) {
        set({ paused });
      }
    },

    setZone: (key, isMounted) =>
      set((state) => {
        if (isMounted === key in state.zones) {
          return state;
        }

        if (isMounted) {
          return { zones: { ...state.zones, [key]: true } };
        }

        const { [key]: _removed, ...zones } = state.zones;

        return { zones };
      }),

    segmentStarted: (segment) => {
      const isFirstShowing = !get().hasShownThisRun;

      set({ running: segment, hasShownThisRun: true });

      return isFirstShowing;
    },

    segmentInterrupted: (segment) => {
      if (get().running === segment) {
        set({ running: null });
      }
    },

    finishSegment: (segment) => {
      const { phase } = get();

      if (phase !== segment) {
        return phase;
      }

      const next = REPLY_DRAFT_TOUR_SEGMENTS[segment].next;

      set({ phase: next });

      return next;
    },

    skip: () => set({ phase: 'done' }),
  }));
}

export function selectIsTourLive(state: ReplyDraftTourStore): boolean {
  return isTourSegment(state.phase);
}

/** The segment waiting to show, once its first target has mounted; `null` otherwise. */
export function selectReadySegment(state: ReplyDraftTourStore): ReplyDraftTourSegment | null {
  const { phase, zones, paused } = state;

  return !paused && isTourSegment(phase) && tourZoneKey(phase, 1) in zones ? phase : null;
}

/**
 * The next or previous mounted zone of a segment, skipping any a draft does not
 * render; `null` past either end. The tour steps over missing zones the same
 * way, so this names the target it will highlight next.
 */
export function neighbourZone(
  state: ReplyDraftTourStore,
  segment: ReplyDraftTourSegment,
  zone: number,
  direction: 1 | -1
): number | null {
  const mounted = Object.keys(state.zones)
    .filter((key) => key.startsWith(`${segment}:`))
    .map((key) => Number(key.slice(segment.length + 1)))
    .filter((candidate) => (direction === 1 ? candidate > zone : candidate < zone))
    .sort((first, second) => (first - second) * direction);

  return mounted[0] ?? null;
}
